import { Suspense, useRef, useState } from 'react'

import { useKeyboardShortcut } from '@molecule/app-keyboard-shortcuts-react'
import { useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Flex } from '@molecule/app-ui-react'

import { usePublishFixedTopOffset } from '../hooks/useFixedTopOffset.js'
import { useIsMobile } from '../hooks/useMediaQuery.js'
import { lazyWithReload } from '../lib/chunk-reload.js'
import { Icon } from './Icon.js'
import { LogoLink } from './LogoText.js'
import { ThemeToggle } from './ThemeToggle.js'
import { UserMenu } from './UserMenu.js'

// The quick-search dialog (packages + templates) loads only when first opened.
const QuickSearch = lazyWithReload(() => import('../pages/catalog/CatalogQuickSearch.js'))

export function Header() {
  const cm = getClassMap()
  const { t } = useTranslation()
  const headerRef = useRef<HTMLElement | null>(null)
  const isMobile = useIsMobile()
  const [searchOpen, setSearchOpen] = useState(false)
  // Mobile: the fixed HealthBanner anchors below this header instead of
  // covering it (see useFixedTopOffset).
  usePublishFixedTopOffset(headerRef, isMobile)
  useKeyboardShortcut('mod+k', () => setSearchOpen(true), {
    description: t('quickSearch.shortcut', undefined, {
      defaultValue: 'Search packages and templates',
    }),
  })
  return (
    <>
      <header ref={headerRef} className={cm.cn(cm.headerBar, cm.headerFixed, 'mol-site-header')}>
        <div
          className={cm.headerInner}
          // headerInner's p-[5px] utility sets a 5px inline inset; pages frame
          // their content at 16px (.mol-page-container, index.css) — these
          // longhands must match that value exactly or page content will not
          // sit flush with the nav above it.
          style={{ paddingLeft: 16, paddingRight: 16 }}
        >
          <Flex align="center" justify="between" className={cm.h('full')}>
            <LogoLink />
            <Flex align="center" gap="sm">
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                aria-label={t('quickSearch.open', undefined, { defaultValue: 'Search (⌘K)' })}
                title={t('quickSearch.open', undefined, { defaultValue: 'Search (⌘K)' })}
                data-mol-id="header-search"
                // Same icon-only chrome recipe as the theme toggle beside it.
                className={cm.cn(cm.themeToggleButton, cm.touchTargetCompact)}
              >
                <Icon name="search" size={20} />
              </button>
              <ThemeToggle />
              <UserMenu />
            </Flex>
          </Flex>
        </div>
        {searchOpen ? (
          <Suspense fallback={null}>
            <QuickSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
          </Suspense>
        ) : null}
      </header>
      {/* The header is FIXED (out of flow) — this in-flow spacer of the same
          height keeps every page's spacing exactly as it was when the header
          sat in flow, so no per-page padding fixes are needed. */}
      <div aria-hidden="true" style={{ height: 40 }} data-mol-id="site-header-spacer" />
    </>
  )
}
